import { config } from '@/config';
import { allowTracking, isServer } from '@/helpers';
import { Confidence, Context } from '@spotify-confidence/sdk';
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useAppSelector } from '.';
import { ConfidenceContext, ConfidenceCtxValue } from './confidenceContext';

export type { ConfidenceCtxValue } from './confidenceContext';
export { ConfidenceContext } from './confidenceContext';

type ConfidenceClient = ReturnType<typeof Confidence.create>;

let confidenceClientSingleton: ConfidenceClient | null = null;

const getOrCreateClient = (): ConfidenceClient | null => {
  if (isServer) {
    return null;
  }

  if (!confidenceClientSingleton) {
    confidenceClientSingleton = Confidence.create({
      clientSecret: config.confidenceClientKey,
      region: 'eu',
      environment: 'client',
      timeout: 2000,
      context: {
        app_environment: config.confidenceEnv,
        platform: 'web',
      },
    });
  }

  return confidenceClientSingleton;
};

export const updateConfidenceContext = (patch: Context, onSuccess?: () => void) => {
  try {
    if (!allowTracking()) return;
    const client = getOrCreateClient();
    if (client) {
      client.setContext(patch);
      onSuccess?.();
    }
  } catch (err) {
    console.warn('Confidence setContext failed:', err);
  }
};

export const useConfidenceClient = (): ConfidenceCtxValue | undefined => {
  const ctx = useContext(ConfidenceContext);
  if (isServer) {
    return null;
  }
  if (!ctx) {
    throw new Error('useConfidenceClient must be used within <ConfidenceExperimentProvider>');
  }
  return ctx;
};

type ProviderProps = { children: React.ReactNode };

export const ConfidenceExperimentProvider = ({ children }: ProviderProps) => {
  const clientRef = useRef<ConfidenceClient | null>(null);
  const [hasDeviceId, setHasDeviceId] = React.useState(false);

  const user = useAppSelector((state) => state.users?.user);

  useEffect(() => {
    if (allowTracking() && user?.emailHash && clientRef.current) {
      clientRef.current.setContext({ user_id: user.emailHash });
    }
  }, [user?.emailHash]);

  useEffect(() => {
    clientRef.current = getOrCreateClient();
  }, []);

  const setDeviceId = useCallback((deviceId: string) => {
    if (isServer) return;
    updateConfidenceContext({ device_id: deviceId }, () => {
      setHasDeviceId(true);
    });
  }, []);

  const updateContext = useCallback((patch: Context) => {
    if (isServer) return;
    updateConfidenceContext(patch);
  }, []);

  const value = useMemo<ConfidenceCtxValue>(() => {
    return {
      client: clientRef?.current,
      updateContext,
      setDeviceId,
      isReady: hasDeviceId,
    };
  }, [updateContext, setDeviceId, hasDeviceId]);

  return <ConfidenceContext.Provider value={value}>{children}</ConfidenceContext.Provider>;
};
